<template>
	<div class="card">

		<div style="margin-bottom:10px;" class="grid ml-0 mr-1">
			<div class="flex flex-auto p-input-icon-left p-input-icon-right mt-1">
				<i class="pi pi-home" />
				<InputText v-model="filterText" placeholder="Property keyword filter.." />
				<i class="pi pi-search" />
			</div>
			<div class="flex align-items-center">
				<a @click="propertyListExcelExport" class="css-image excel-icon mt-1" v-tooltip.top="'Export to Excel'"></a>

				<div class="flex ml-4 mr-4">
					<a class="css-image" v-bind:class="filterNorthIsland ? 'north-island-icon':'north-island-disabled-icon'" v-tooltip.top="'Show properties in North Island'" style="position: absolute; margin-top: -15px; height: 20px; width: 30px;" @click="filterNorthIsland = !filterNorthIsland;filterSouthIsland = false;" />
					<a class="css-image" v-bind:class="filterSouthIsland ? 'south-island-icon':'south-island-disabled-icon'" v-tooltip.top="'Show properties in South Island'" style="position: absolute; margin-top: 5px; height: 20px; width: 30px; "  @click="filterSouthIsland = !filterSouthIsland;filterNorthIsland = false;"/>
				</div>

				<InputSwitch v-model="onlyActive" class="ml-4 mr-1 mt-1" />
				<a class="mr-4 mt-1" @click="onlyActive=!onlyActive">Only active</a>
				<Button icon="pi pi-plus" label="New" @click="newProperty" class="p-button-rounded mt-1" />
			</div>
		</div>
		<Message v-if="propertyListError" severity="error">{{propertyListError}}</Message>

		<div class="propertylist-container">

			<DataTable :value="filteredPropertyList"
					   datakey="propertyId"
					   :paginator="true"
					   :rows="100"
					   :scrollable="true"
					   scrollHeight="flex"
					   stripedRows
					   class="p-datatable-sm"
					   :rowClass="applyRowClass"
					   :paginatorTemplate="paginatorTemplate"
					   :rowsPerPageOptions="[10,20,50,100,200]"
					   responsiveLayout="scroll"
					   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}">

				<Column header="ID" style="flex-grow:0;flex-basis:80px">
					<template #body="{data}">
						<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">{{data.propertyId}}</router-link>
					</template>
				</Column>

				<Column header="Property">
					<template #body="{data}">
						<div>
							<router-link :to="{ name: 'PropertyDetails', params: { propertyId: data.propertyId }}">{{data.propertyNameAndLocation}}</router-link>
							<Badge v-if="!data.active" class="small-grey-badge ml-1" value="inactive" />
							<div v-if="data.buildingName" class="text-color-secondary text-sm">{{data.buildingName}}</div>
						</div>
					</template>
				</Column>

				<Column header="Location" class="flex" style="flex-basis:100px;">
					<template #body="{data}">
						<span>{{data.propertyLocation}}</span>
						
						<Badge class="css-image new-zealand-icon island-badge ml-1" v-tooltip.top="'North & South Island'" v-if="data.isle == 'NI SI'" />

						<Badge class="css-image north-island-icon island-badge ml-1" v-tooltip.top="'North Island'" v-else-if="data.isle == 'NI'" />

						<Badge class="css-image south-island-icon island-badge ml-1" v-tooltip.top="'South Island'" v-else-if="data.isle == 'SI'" />
					</template>
				</Column>

				<Column class="hidden lg:block" field="complianceSchedule" header="BWOF #" style="flex-grow:0;flex-basis:100px;" />

				<Column class="hidden lg:block text-center" field="active" header="Active" style="flex-grow:0;flex-basis:60px;">
					<template #body="{data}">
						<i class="pi pi-check" v-if="data.active"></i>
					</template>
				</Column>

				<Column class="hidden md:block" style="flex-grow: 0; flex-basis: 120px;">
					<template #body="{data}">
						<a style="cursor:pointer" @click="clickSidebar(data)"><i class="pi pi-info-circle"></i></a>
					</template>
				</Column>

			</DataTable>

			<!-- Details side bar-->
			<Sidebar v-model:visible="propertyListSidebar" :baseZIndex="1000" position="right">
				<h3>{{sidebarDataName}}</h3>

				<Timeline :value="services">
					<template #content="slotProps">
						<div style="padding-bottom:15px;">
							{{slotProps.item.serviceName}}
						</div>
					</template>
					<template #opposite="slotProps">
						<small class="p-text-secondary">{{formattedDate(slotProps.item.whenCreated)}}</small>
					</template>
				</Timeline>
			</Sidebar>
		</div>
	</div>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { GetPropertyListQuery, PropertyData, PropertyServiceData, PropertySummary } from '@/api-client/api-client'
	import { CompassApi } from '@/api-client/CompassApiProxy'
	import { format, isValid } from 'date-fns';
	import filterListBySearchString from '../utils/StringSearch';
	import { DownloadFromFileResponse } from '../utils/DownloadFromFileResponse';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
	import Toast from '@/utils/Toast';

	Vue.registerHooks([
		'beforeRouteEnter',
	]);

	@Options({
		name: 'PropertyList',
		components: {
		},
		props: {
		}
	})
	export default class PropertyList extends Vue {

		// Data
		filterText = ""
		propertyListSidebar = false;
		sidebarDataName: string | null =  null;
		services: PropertyServiceData[] = [];
		onlyActive = true;
		filterNorthIsland = false;
		filterSouthIsland = false;

		// Event each time entering this module, ensure list updated
		  beforeRouteEnter(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			next((vm: Vue) => {
				// access to component instance via `vm`
				// ensure company list downloaded
				vm.$store.dispatch('downloadPropertyList');
			})
		}

		// Action
		click(data: PropertySummary) {
			this.$router.push({
				name: 'PropertyDetails',
				params: { propertyId: data.propertyId }
			});
		}

		clickSidebar(data: PropertyData) {
			if (!data.propertyId)
				return;

			this.propertyListSidebar = true;

			if (data.propertyName) 
				this.sidebarDataName = data.propertyName;

			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.getPropertyById(data.propertyId)
				.then(result => {
					if (result.services != null) {
						this.services = result.services;
					}
				})
				.catch(error => {
					Toast.ApiError('Error retrieving property ' + data.propertyId, error);
				});
		}

		newProperty() {
			this.$router.push({
				name: 'NewProperty'
			});
		}

		propertyListExcelExport() {

			// Prepare payload to send to server
			const propertyListData = new GetPropertyListQuery();
			propertyListData.queryString = this.filterText;
			propertyListData.onlyActive = this.onlyActive;
			propertyListData.filterNorthIsland = this.filterNorthIsland;
			propertyListData.filterSouthIsland = this.filterSouthIsland;

			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.propertyListExcelExport(propertyListData)
				.then(result => {

					// Download Popup
					if (result)
						DownloadFromFileResponse(result);

					Toast.RemoveAllGroups();
					Toast.Success('Property List Excel export', "The export was successful.");
				})
				.catch(error => {
					Toast.ApiError("Error exporting to excel", error);
				});
		}

		// Helpers
		applyRowClass(data: PropertySummary) {
			return data.active ? "" : "row-inactive";
		}

		// Computed
		get filteredPropertyList() {
			let list = this.$store.state.propertyList.data;
			if (this.onlyActive)
				list = list.filter(property => property.active === true);

			if (this.filterNorthIsland)
				list = list.filter(property => property.isle?.includes("NI"));

			if (this.filterSouthIsland)
				list = list.filter(property => property.isle?.includes("SI"));

			list = filterListBySearchString(list, this.filterText, (property: PropertySummary) => property.propertyId + ' ' + property.propertyNameAndLocation + ' ' + property.propertyLocation + ' ' + property.customerPropertyRef + ' ' + property.buildingName + ' ' + property.complianceSchedule);
			return list;
		}

		get propertyListError() {
			return this.$store.state.propertyList.errorMessage;
		}

		// Computed isDesktop
		get isDesktop() {
			return window.innerWidth >= 992;
		}

		// Computed to simplify paginator on mobile
		get paginatorTemplate() {
			return this.isDesktop ?
				"CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
				: "PrevPageLink NextPageLink"
		}

		formattedDate(value: Date) {
			if (!isValid(value)) return;

			return format(value, 'dd MMM yyyy');
		}
	}
</script>

<style scoped>
	.propertylist-container {
		width: 100%;
		height: calc(100vh - 260px);
	}
</style>